import React, { memo } from "react";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/layout";
import {
  HStack,
  IconButton,
  SkeletonText,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Link } from "react-router-dom";
import { CopyText } from "../Utilities/CopyText";

interface ApplicationsTableProps {
  applications: CherryHubAdminApi.PlatformApplicationSummaryItem[] | null;
  isLoading: boolean;
  debouncedQuery: string;
  disableActions?: boolean;
  onClickEditApplication: (
    application: CherryHubAdminApi.PlatformApplicationSummaryItem
  ) => void;
  onClickDeleteApplication: (
    application: CherryHubAdminApi.PlatformApplicationSummaryItem
  ) => void;
  onClickRestoreApplication: (
    application: CherryHubAdminApi.PlatformApplicationSummaryItem
  ) => void;
}

export const ApplicationsTable = memo(
  ({
    applications,
    isLoading,
    disableActions,
    debouncedQuery,
    onClickEditApplication,
    onClickDeleteApplication,
    onClickRestoreApplication,
  }: ApplicationsTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th whiteSpace="nowrap">Application Id</Th>
              <Th whiteSpace="nowrap">Name</Th>
              <Th whiteSpace="nowrap">Status</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (applications ?? []).map((application) => (
                <Tr key={application.Meta?.Id}>
                  <Td width="25%" whiteSpace="nowrap" title="Edit application.">
                    <CopyText
                      text={application.Meta?.Id!}
                      copyOnIcon={true}
                      hover={true}
                    >
                      <ChakraLink
                        as={Link}
                        to={`/admin/applications/${application.Meta?.Id}`}
                      >
                        {application.Meta?.Id}
                      </ChakraLink>
                    </CopyText>
                  </Td>
                  <Td
                    whiteSpace="nowrap"
                    title="Edit application."
                    _hover={
                      !application.Meta?.IsDeleted
                        ? { textDecoration: "underline", cursor: "pointer" }
                        : {}
                    }
                    onClick={() =>
                      !application.Meta?.IsDeleted &&
                      onClickEditApplication(application)
                    }
                  >
                    {!!application.Meta?.IsDeleted && (
                      <Tag size="sm" mr="2">
                        Deleted
                      </Tag>
                    )}
                    {application.DisplayName}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={application.IsEnabled ? "" : "cherryButton.500"}
                      color={application.IsEnabled ? "" : "white"}
                    >
                      {application.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {(application.Meta?.IsDeleted && (
                        <IconButton
                          aria-label="Restore application."
                          title="Restore application."
                          icon={<RepeatClockIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onClickRestoreApplication(application)}
                        />
                      )) || (
                        <IconButton
                          marginLeft="1"
                          aria-label="Delete application."
                          title="Delete application."
                          icon={<DeleteIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onClickDeleteApplication(application)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {applications?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={4} textAlign="center">
                  No application found matching query '{debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="25%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="65%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
